// ChaCha20-Poly1305 decryption + URL-fragment CEK extraction.
//
// The patient's iOS encrypts the share bundle with CryptoKit
// `ChaChaPoly.seal`, producing the "combined" representation:
//
//   [nonce: 12 bytes][ciphertext: N bytes][tag: 16 bytes]
//
// The 32-byte CEK rides in the URL fragment after `#k=<base64url>`
// (RFC 3986 fragments never reach the server). The recipient web
// extracts it from `location.hash` and uses WebCrypto to decrypt.
//
// Per Linear DEV2-419, ADR-Platform-0008, SPEC-ALPHA-SDK-034 v0.2
// (Option B: presence + HTTPS bundle + URL-fragment CEK).
//
// WebCrypto ChaCha20-Poly1305 is NOT universal (Safari/iOS + in-app
// WebViews are patchy), so `decryptBundle` tries native first and falls
// back to the pure-JS `@noble/ciphers` implementation — decryption then
// works on every browser.

import { chacha20poly1305 } from "@noble/ciphers/chacha";

const NONCE_LEN = 12;
const TAG_LEN = 16;
const KEY_LEN = 32;

/**
 * Pull the CEK out of the URL fragment. Format: `#k={base64url}` or
 * `#...&k={base64url}` (other params allowed). Returns null if
 * absent or malformed.
 */
export function extractCEKFromHash(hash: string): Uint8Array | null {
  const fragment = hash.replace(/^#/, "");
  if (!fragment) return null;
  const params = new URLSearchParams(fragment);
  const k = params.get("k");
  if (!k) return null;
  return decodeBase64Url(k);
}

/** base64url → bytes. Returns null on malformed input or wrong length. */
function decodeBase64Url(s: string): Uint8Array | null {
  // base64url → base64
  const padded =
    s.replace(/-/g, "+").replace(/_/g, "/") +
    "==".slice((s.length + 2) % 4);
  let raw: string;
  try {
    raw = atob(padded);
  } catch {
    return null;
  }
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes.length === KEY_LEN ? bytes : null;
}

/**
 * Decrypt a ChaCha20-Poly1305 sealed-box payload.
 *
 * Input format matches Apple CryptoKit's `ChaChaPoly.seal().combined`:
 * `[nonce: 12 bytes][ciphertext][tag: 16 bytes]`.
 *
 * Throws if the payload is too short, the WebCrypto algorithm is
 * unavailable, or the auth tag fails (corrupted ciphertext, wrong key).
 */
export async function decryptBundle(
  cekBytes: Uint8Array,
  sealed: Uint8Array,
): Promise<Uint8Array> {
  if (sealed.length < NONCE_LEN + TAG_LEN) {
    throw new DecryptError(
      "payload_too_short",
      `Bundle payload is ${sealed.length} bytes; need at least ${NONCE_LEN + TAG_LEN}.`,
    );
  }
  const nonce = sealed.slice(0, NONCE_LEN);
  const ciphertextWithTag = sealed.slice(NONCE_LEN);

  // Fast path: native WebCrypto ChaCha20-Poly1305 (Chrome/Edge, recent
  // Firefox). It is NOT universally available — Safari/iOS and in-app
  // WebViews are patchy — so on any failure we fall back to a pure-JS
  // implementation rather than dead-ending the recipient.
  try {
    const key = await crypto.subtle.importKey(
      "raw",
      cekBytes as BufferSource,
      { name: "ChaCha20-Poly1305" },
      false,
      ["decrypt"],
    );
    const plaintext = await crypto.subtle.decrypt(
      { name: "ChaCha20-Poly1305", iv: nonce as BufferSource },
      key,
      ciphertextWithTag as BufferSource,
    );
    return new Uint8Array(plaintext);
  } catch {
    // WebCrypto lacks ChaCha20-Poly1305 here, or the tag failed. Retry with
    // the pure-JS cipher below; a genuine wrong-key/tamper still throws there.
  }

  // Fallback: @noble/ciphers IETF ChaCha20-Poly1305 (32-byte key, 12-byte
  // nonce, ciphertext-with-appended-tag) — matches CryptoKit's `combined`
  // layout after the nonce prefix is stripped above. Works on every browser.
  try {
    return chacha20poly1305(cekBytes, nonce).decrypt(ciphertextWithTag);
  } catch {
    throw new DecryptError(
      "auth_failed",
      "Bundle could not be decrypted. The link may have been tampered with.",
    );
  }
}

export type DecryptErrorCode =
  | "payload_too_short"
  | "algorithm_unsupported"
  | "auth_failed";

export class DecryptError extends Error {
  constructor(
    public readonly code: DecryptErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "DecryptError";
  }
}
